import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api, { fmtMoney, fmtDate, fileUrl } from "@/lib/api";
import StatusBadge from "@/components/StatusBadge";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import { Sparkles, ArrowLeft, Send, FileText, Home, ExternalLink, RefreshCw, ChevronLeft, ChevronRight, X, FileSignature, Trash2, Pencil, Lock } from "lucide-react";

const LOCKED_STATUSES = new Set(["offered", "closed"]);
import ConfirmDialog from "@/components/ConfirmDialog";

const STATUSES = ["new", "reviewing", "offered", "closed"];

function Lightbox({ photos, index, onClose, onNav }) {
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
      if (e.key === "ArrowRight") onNav(1);
      if (e.key === "ArrowLeft") onNav(-1);
    };
    window.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [onClose, onNav]);

  const p = photos[index];
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center"
      style={{ background: "rgba(0,0,0,0.92)", backdropFilter: "blur(8px)" }}
      onClick={onClose}
      data-testid="photo-lightbox"
    >
      <button
        onClick={onClose}
        className="absolute top-5 right-5 p-2 rounded-full transition hover:bg-white/10"
        style={{ color: "var(--text)" }}
        data-testid="lightbox-close"
      >
        <X size={22} />
      </button>

      {photos.length > 1 && (
        <>
          <button
            onClick={(e) => { e.stopPropagation(); onNav(-1); }}
            className="absolute left-4 sm:left-8 p-3 rounded-full transition hover:bg-white/10"
            style={{ color: "var(--lime)" }}
            data-testid="lightbox-prev"
          >
            <ChevronLeft size={28} />
          </button>
          <button
            onClick={(e) => { e.stopPropagation(); onNav(1); }}
            className="absolute right-4 sm:right-8 p-3 rounded-full transition hover:bg-white/10"
            style={{ color: "var(--lime)" }}
            data-testid="lightbox-next"
          >
            <ChevronRight size={28} />
          </button>
        </>
      )}

      <img
        src={fileUrl(p.id)}
        alt={p.original_filename}
        className="rounded-2xl"
        style={{ maxHeight: "85vh", maxWidth: "88vw", objectFit: "contain", boxShadow: "0 24px 80px rgba(0,0,0,0.6)" }}
        onClick={(e) => e.stopPropagation()}
        data-testid="lightbox-image"
      />

      <div
        className="absolute bottom-6 left-1/2 -translate-x-1/2 font-mono text-xs px-3 py-1.5 rounded-full"
        style={{ background: "rgba(20,20,20,0.85)", color: "var(--text-muted)", border: "1px solid var(--border)" }}
        data-testid="lightbox-counter"
      >
        {index + 1} / {photos.length}
      </div>
    </div>
  );
}

export default function DealDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [deal, setDeal] = useState(null);
  const [loading, setLoading] = useState(true);
  const [aiBusy, setAiBusy] = useState(false);
  const [comment, setComment] = useState("");
  const [adminNotes, setAdminNotes] = useState("");
  const [statusBusy, setStatusBusy] = useState(false);
  const [askProfit, setAskProfit] = useState(false);
  const [profitInput, setProfitInput] = useState("");
  const [lightbox, setLightbox] = useState(null); // photo index or null
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [editing, setEditing] = useState(false);
  const [history, setHistory] = useState([]);

  const loadHistory = async (dealId) => {
    try {
      const { data } = await api.get(`/deals/${dealId}/history`);
      setHistory(data || []);
    } catch (e) { /* ignore */ }
  };

  const deleteDeal = async () => {
    try {
      await api.delete(`/deals/${id}`);
      toast.success("Deal deleted");
      navigate(user?.is_admin ? "/admin" : "/deals");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Delete failed");
    }
  };

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/deals/${id}`);
      setDeal(data);
      setAdminNotes(data.admin_notes || "");
      loadHistory(id);
    } catch (e) {
      toast.error("Could not load deal");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

  const requestAI = async () => {
    setAiBusy(true);
    try {
      const { data } = await api.post(`/deals/${id}/ai-offer`);
      setDeal({ ...deal, ai_offer_suggestion: data.ai_offer_suggestion });
      toast.success("Team analysis ready");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "AI request failed");
    } finally {
      setAiBusy(false);
    }
  };

  const updateStatus = async (status, profit) => {
    if (status === "closed" && profit === undefined) {
      setAskProfit(true);
      return;
    }
    setStatusBusy(true);
    try {
      const body = { status };
      if (profit !== undefined && profit !== null && profit !== "") body.profit = Number(profit);
      const { data } = await api.patch(`/deals/${id}`, body);
      setDeal({ ...deal, ...data });
      toast.success(`Status: ${status}`);
    } catch (e) {
      toast.error("Update failed");
    } finally {
      setStatusBusy(false);
      setAskProfit(false);
    }
  };

  const saveNotes = async () => {
    try {
      const { data } = await api.patch(`/deals/${id}`, { admin_notes: adminNotes });
      setDeal({ ...deal, ...data });
      toast.success("Notes saved");
    } catch (e) {
      toast.error("Save failed");
    }
  };

  const sendComment = async (e) => {
    e.preventDefault();
    if (!comment.trim()) return;
    try {
      const { data } = await api.post(`/deals/${id}/comments`, { body: comment });
      setDeal({ ...deal, comments: [...(deal.comments || []), data] });
      setComment("");
    } catch (e) {
      toast.error("Comment failed");
    }
  };

  if (loading) return <div className="text-sm" style={{ color: "var(--text-muted)" }}>Loading...</div>;
  if (!deal) return <div>Not found.</div>;

  const arv = Number(deal.arv || 0);
  const repairs = Number(deal.repair_estimate || 0);
  const ask = Number(deal.asking_price || 0);
  const fee = Number(deal.assignment_fee || 0);
  const mao = arv > 0 ? arv * 0.7 - repairs - fee : 0;
  const margin = mao - ask;

  return (
    <div data-testid="deal-detail-page">
      <button onClick={() => navigate(-1)} className="flex items-center gap-1 text-sm mb-6" style={{ color: "var(--text-muted)" }} data-testid="back-link">
        <ArrowLeft size={14} /> Back
      </button>

      <div className="flex items-start justify-between gap-4 flex-wrap mb-3">
        <div>
          <div className="section-tag mb-3"><span className="dot" /> {deal.submission_type === "jv" ? "JV Deal" : "Property Analysis"}</div>
          <h1 className="h-display text-3xl sm:text-4xl">{deal.property_address}</h1>
          <p className="text-sm mt-2 font-mono" style={{ color: "var(--text-muted)" }}>
            {fmtDate(deal.created_at)} · {deal.user_name || deal.user_email}
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-3 deal-actions">
          <button
            onClick={() => {
              const q = [
                `Deal: ${deal.property_address}`,
                deal.arv ? `ARV: $${Number(deal.arv).toLocaleString()}` : null,
                deal.repair_estimate ? `Repairs: $${Number(deal.repair_estimate).toLocaleString()}` : null,
                deal.asking_price ? `Asking: $${Number(deal.asking_price).toLocaleString()}` : null,
                deal.condition ? `Condition: ${deal.condition}` : null,
                deal.seller_motivation ? `Seller situation: ${deal.seller_motivation}` : null,
                "",
                "Is this worth submitting, and what should I do next?",
              ].filter(Boolean).join("\n");
              navigate(`/coach?ask=${encodeURIComponent(q)}`);
            }}
            className="btn-ghost flex items-center gap-2 text-sm"
            data-testid="ask-coach-about-deal"
          >
            <Sparkles size={14} /> Ask coach
          </button>
          <button
            onClick={() => navigate(`/contracts?deal=${deal.id}`)}
            className="btn-primary flex items-center gap-2 text-sm"
            data-testid="generate-contract-button"
          >
            <FileSignature size={14} /> Create Contract
          </button>
          <StatusBadge status={deal.status} />
          {!user?.is_admin && deal.user_id === user?.id && LOCKED_STATUSES.has(deal.status) && (
            <span
              className="flex items-center gap-1.5 font-mono text-[11px] px-2.5 py-1.5 rounded-xl"
              style={{ color: "var(--text-dim)", border: "1px solid var(--border)" }}
              title="Your coach has moved this forward, so it can't be changed"
              data-testid="deal-locked-note"
            >
              <Lock size={11} /> Locked
            </span>
          )}
          {(user?.is_admin || (deal.user_id === user?.id && !LOCKED_STATUSES.has(deal.status))) && (
            <>
              <button
                onClick={() => setEditing(true)}
                className="p-2 rounded-xl hover:bg-white/5 transition"
                style={{ color: "var(--text-muted)", border: "1px solid var(--border)" }}
                title="Edit this submission"
                data-testid="edit-deal-button"
              >
                <Pencil size={14} />
              </button>
              <button
                onClick={() => setConfirmDelete(true)}
                className="p-2 rounded-xl hover:bg-white/5 transition"
                style={{ color: "#f87171", border: "1px solid rgba(248,113,113,0.3)" }}
                title="Delete deal"
                data-testid="delete-deal-button"
              >
                <Trash2 size={14} />
              </button>
            </>
          )}
        </div>
      </div>

      <ConfirmDialog
        open={confirmDelete}
        title={`Delete this deal?`}
        description={
          user?.is_admin
            ? `This permanently removes "${deal.property_address}" and all its comments. The student will lose access. This cannot be undone.`
            : `This permanently removes "${deal.property_address}" and any coach notes on it. This cannot be undone.`
        }
        destructive
        confirmLabel="Delete Deal"
        onCancel={() => setConfirmDelete(false)}
        onConfirm={deleteDeal}
      />

      {askProfit && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4"
          style={{ background: "rgba(0,0,0,0.8)", backdropFilter: "blur(6px)" }}
          onClick={() => setAskProfit(false)}
          data-testid="close-profit-modal"
        >
          <div
            className="w-full max-w-sm rounded-3xl p-6"
            style={{ background: "var(--surface)", border: "1px solid var(--border-lime)" }}
            onClick={(e) => e.stopPropagation()}
          >
            <h2 className="h-display text-xl mb-1.5">Mark this deal closed</h2>
            <p className="text-sm mb-4" style={{ color: "var(--text-muted)" }}>
              What was the assignment fee or profit on this one? It feeds the student's KPIs.
            </p>
            <label className="field-label">Profit ($)</label>
            <input
              className="input-base"
              inputMode="decimal"
              autoFocus
              value={profitInput}
              placeholder="10,000"
              onChange={(e) => setProfitInput(e.target.value.replace(/[^0-9.,]/g, ""))}
              data-testid="close-profit-input"
            />
            <div className="flex items-center justify-end gap-3 mt-5">
              <button onClick={() => updateStatus("closed", 0)} className="btn-ghost text-sm" data-testid="close-profit-skip">
                Skip
              </button>
              <button
                onClick={() => updateStatus("closed", profitInput.replace(/,/g, "") || 0)}
                disabled={statusBusy}
                className="btn-primary text-sm"
                data-testid="close-profit-save"
              >
                Mark closed
              </button>
            </div>
          </div>
        </div>
      )}

      {editing && (
        <EditDealModal
          deal={deal}
          onClose={() => setEditing(false)}
          onSaved={(fresh) => { setDeal(fresh); setEditing(false); loadHistory(fresh.id); }}
        />
      )}

      <div className="grid lg:grid-cols-3 gap-6 mt-8">
        <div className="lg:col-span-2 space-y-6">
          {/* Numbers */}
          <div className="surface-card p-6">
            <h2 className="font-mono text-xs uppercase tracking-wider mb-4" style={{ color: "var(--lime)" }}>The numbers</h2>
            <div className="grid sm:grid-cols-3 gap-4 mb-4">
              <Stat label="ARV" v={fmtMoney(deal.arv)} />
              <Stat label="Asking" v={fmtMoney(deal.asking_price)} />
              <Stat label="Repairs" v={fmtMoney(deal.repair_estimate)} />
              <Stat label="MAO (70%)" v={fmtMoney(mao)} accent />
              <Stat label="Margin" v={fmtMoney(margin)} accent={margin >= 0} negative={margin < 0} />
              <Stat label="Fee goal" v={fmtMoney(deal.assignment_fee)} />
            </div>
            <div className="grid sm:grid-cols-2 gap-4 text-sm pt-4 divider-faint">
              <div><span style={{ color: "var(--text-muted)" }}>Type:</span> {deal.property_type}</div>
              <div><span style={{ color: "var(--text-muted)" }}>Condition:</span> {deal.condition || "—"}</div>
              <div><span style={{ color: "var(--text-muted)" }}>Beds/Baths:</span> {deal.beds || "—"} / {deal.baths || "—"}</div>
              {deal.submission_type === "jv" && (
                <div><span style={{ color: "var(--text-muted)" }}>Closing date:</span> {deal.closing_date || "—"}</div>
              )}
            </div>
            {deal.seller_motivation && (
              <div className="mt-4 pt-4 divider-faint">
                <div className="font-mono text-xs uppercase tracking-wider mb-2" style={{ color: "var(--text-muted)" }}>Seller situation</div>
                <p className="text-sm whitespace-pre-wrap">{deal.seller_motivation}</p>
              </div>
            )}
            {deal.notes && (
              <div className="mt-4 pt-4 divider-faint">
                <div className="font-mono text-xs uppercase tracking-wider mb-2" style={{ color: "var(--text-muted)" }}>Student notes</div>
                <p className="text-sm whitespace-pre-wrap">{deal.notes}</p>
              </div>
            )}
          </div>

          <EditHistory entries={history} />

          {/* Photos link (Google Drive / Dropbox / iCloud) */}
          {deal.photos_link && (
            <div className="surface-card p-6" data-testid="photos-link-card">
              <h2 className="font-mono text-xs uppercase tracking-wider mb-4" style={{ color: "var(--lime)" }}>Photos</h2>
              <a
                href={deal.photos_link}
                target="_blank"
                rel="noreferrer"
                className="flex items-center gap-2 p-3 rounded-2xl transition hover:opacity-80"
                style={{ background: "var(--surface2)", border: "1px solid var(--border)" }}
                data-testid="photos-link-anchor"
              >
                <ExternalLink size={14} style={{ color: "var(--lime)" }} />
                <span className="text-sm truncate flex-1">{deal.photos_link}</span>
                <span className="text-xs font-mono" style={{ color: "var(--text-muted)" }}>Open →</span>
              </a>
            </div>
          )}

          {/* Photos (legacy uploads — kept for old deals) */}
          {deal.photos?.length > 0 && (
            <div className="surface-card p-6">
              <h2 className="font-mono text-xs uppercase tracking-wider mb-4" style={{ color: "var(--lime)" }}>Photos</h2>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                {deal.photos.map((p, i) => (
                  <button
                    key={p.id}
                    onClick={() => setLightbox(i)}
                    className="block aspect-square rounded-2xl overflow-hidden cursor-zoom-in transition hover:opacity-80"
                    style={{ background: "var(--surface2)" }}
                    data-testid={`photo-thumb-${p.id}`}
                  >
                    <img src={fileUrl(p.id)} alt={p.original_filename} className="w-full h-full object-cover" data-testid={`photo-${p.id}`} />
                  </button>
                ))}
              </div>
              {lightbox !== null && (
                <Lightbox
                  photos={deal.photos}
                  index={lightbox}
                  onClose={() => setLightbox(null)}
                  onNav={(dir) => setLightbox((i) => (i + dir + deal.photos.length) % deal.photos.length)}
                />
              )}
            </div>
          )}

          {/* Docs */}
          {deal.docs?.length > 0 && (
            <div className="surface-card p-6">
              <h2 className="font-mono text-xs uppercase tracking-wider mb-4" style={{ color: "var(--lime)" }}>Documents</h2>
              <div className="space-y-2">
                {deal.docs.map((d) => (
                  <a key={d.id} href={fileUrl(d.id)} target="_blank" rel="noreferrer" className="flex items-center gap-2 p-3 rounded-2xl" style={{ background: "var(--surface2)", border: "1px solid var(--border)" }}>
                    <FileText size={14} style={{ color: "var(--lime)" }} />
                    <span className="text-sm">{d.original_filename}</span>
                  </a>
                ))}
              </div>
            </div>
          )}

          {/* AI Triage (admin only, JV deals) */}
          {user?.is_admin && deal.ai_triage && (
            <div className="surface-card p-6" data-testid="ai-triage-card">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <Sparkles size={15} style={{ color: "var(--lime)" }} />
                  <h2 className="font-mono text-xs uppercase tracking-wider" style={{ color: "var(--lime)" }}>
                    AI analysis
                  </h2>
                </div>
                <span className="font-mono text-xs flex items-center gap-2" style={{ color: "var(--text-muted)" }}>
                  {deal.ai_triage.confidence && (
                    <span
                      className="px-2 py-0.5 rounded-full text-[10px] uppercase tracking-wider"
                      style={{
                        background: deal.ai_triage.confidence === "high" ? "rgba(74,222,128,0.12)" : "var(--surface2)",
                        color: deal.ai_triage.confidence === "high" ? "#4ade80" : "var(--text-muted)",
                        border: "1px solid var(--border)",
                      }}
                      data-testid="ai-triage-confidence"
                    >
                      {deal.ai_triage.confidence} confidence
                    </span>
                  )}
                  Score: <span style={{ color: "var(--lime)" }}>{deal.ai_triage.score}/10</span>
                </span>
              </div>
              {deal.ai_triage.math && (
                <p
                  className="font-mono text-xs mb-3 p-3 rounded-xl"
                  style={{ background: "var(--surface2)", color: "var(--text-muted)" }}
                  data-testid="ai-triage-math"
                >
                  {deal.ai_triage.math}
                </p>
              )}
              <p className="text-sm leading-relaxed mb-3">{deal.ai_triage.summary}</p>
              {deal.ai_triage.concerns?.length > 0 && (
                <div>
                  <div className="font-mono text-[10px] uppercase tracking-wider mb-2" style={{ color: "var(--text-muted)" }}>Concerns</div>
                  <ul className="text-xs space-y-1" style={{ color: "var(--text-muted)" }}>
                    {deal.ai_triage.concerns.map((c, i) => <li key={i}>• {c}</li>)}
                  </ul>
                </div>
              )}
              {deal.ai_triage.missing_info?.length > 0 && (
                <div className="mt-3" data-testid="ai-triage-missing">
                  <div className="font-mono text-[10px] uppercase tracking-wider mb-2" style={{ color: "var(--text-muted)" }}>
                    Not provided by the student
                  </div>
                  <ul className="text-xs space-y-1" style={{ color: "var(--text-dim)" }}>
                    {deal.ai_triage.missing_info.map((c, i) => <li key={i}>• {c}</li>)}
                  </ul>
                </div>
              )}
            </div>
          )}

          {/* Bricked auto-comp (admin only) */}
          {user?.is_admin && (
            <BrickedCard
              dealId={id}
              bricked={deal.bricked}
              onResult={(b) => setDeal((d) => ({ ...d, bricked: b }))}
            />
          )}

          {/* AI Offer */}
          <div className="ai-box">
            <div className="flex items-center justify-between mb-4 relative z-10">
              <div className="flex items-center gap-2">
                <Sparkles size={16} style={{ color: "var(--lime)" }} />
                <div className="font-mono text-xs uppercase tracking-wider" style={{ color: "var(--lime)" }}>
                  AI analysis
                </div>
              </div>
              <button onClick={requestAI} className="btn-primary text-sm flex items-center gap-2" disabled={aiBusy} data-testid="ai-offer-button">
                {aiBusy ? <span className="spinner" /> : <Sparkles size={14} />}
                {deal.ai_offer_suggestion ? "Re-run analysis" : "Run analysis"}
              </button>
            </div>
            <div className="relative z-10">
              {deal.ai_offer_suggestion ? (
                <div className="text-sm leading-relaxed" data-testid="ai-result">
                  <AiText text={deal.ai_offer_suggestion} />
                </div>
              ) : (
                <p className="text-sm" style={{ color: "var(--text-muted)" }}>
                  Press "Run analysis" for an AI read on the spread: the MAO, what to offer, and how to get it under contract.
                </p>
              )}
            </div>
          </div>

          {/* Comments */}
          <div className="surface-card p-6">
            <h2 className="font-mono text-xs uppercase tracking-wider mb-4" style={{ color: "var(--lime)" }}>Conversation</h2>
            <div className="space-y-3 mb-4">
              {(deal.comments || []).length === 0 ? (
                <p className="text-sm" style={{ color: "var(--text-muted)" }}>No messages yet.</p>
              ) : (
                deal.comments.map((c) => {
                  let displayName;
                  if (c.author_is_admin) {
                    displayName = user?.is_admin ? "Me" : "Salvador";
                  } else {
                    displayName = user?.is_admin ? (c.author_name || c.author_email || "Student") : "Me";
                  }
                  return (
                  <div key={c.id} className="p-4 rounded-2xl" style={{ background: c.author_is_admin ? "var(--lime-glow)" : "var(--surface2)", border: `1px solid ${c.author_is_admin ? "var(--border-lime)" : "var(--border)"}` }}>
                    <div className="flex items-center gap-2 mb-1">
                      <span className="font-mono text-xs" style={{ color: c.author_is_admin ? "var(--lime)" : "var(--text-muted)" }}>
                        {displayName}
                      </span>
                      <span className="text-xs" style={{ color: "var(--text-dim)" }}>{fmtDate(c.created_at)}</span>
                    </div>
                    <p className="text-sm whitespace-pre-wrap">{c.body}</p>
                  </div>
                  );
                })
              )}
            </div>
            <form onSubmit={sendComment} className="flex gap-2">
              <input
                value={comment}
                onChange={(e) => setComment(e.target.value)}
                placeholder="Reply..."
                className="input-base"
                data-testid="comment-input"
              />
              <button type="submit" className="btn-primary flex items-center gap-1" disabled={!comment.trim()} data-testid="comment-send-button">
                <Send size={14} />
              </button>
            </form>
          </div>
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          <div className="surface-card p-6">
            <h2 className="font-mono text-xs uppercase tracking-wider mb-4" style={{ color: "var(--lime)" }}>Submitter</h2>
            <div className="space-y-2 text-sm">
              <div><span style={{ color: "var(--text-muted)" }}>Name:</span> {deal.contact_name}</div>
              <div><span style={{ color: "var(--text-muted)" }}>Email:</span> {deal.user_email}</div>
              <div><span style={{ color: "var(--text-muted)" }}>Phone:</span> {deal.contact_phone || "—"}</div>
            </div>
          </div>

          {user?.is_admin && (
            <div className="surface-card p-6" data-testid="admin-controls">
              <h2 className="font-mono text-xs uppercase tracking-wider mb-4" style={{ color: "var(--lime)" }}>Admin controls</h2>
              <div className="mb-5">
                <label className="field-label">Status</label>
                <div className="flex flex-wrap gap-2">
                  {STATUSES.map((s) => (
                    <button
                      key={s}
                      onClick={() => updateStatus(s)}
                      disabled={statusBusy}
                      className="text-xs uppercase px-3 py-1.5 rounded-full font-mono tracking-wider"
                      style={{
                        background: deal.status === s ? "var(--lime-glow)" : "var(--surface2)",
                        color: deal.status === s ? "var(--lime)" : "var(--text-muted)",
                        border: deal.status === s ? "1px solid var(--border-lime)" : "1px solid var(--border)",
                      }}
                      data-testid={`status-button-${s}`}
                    >
                      {s}
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <label className="field-label">Internal admin notes</label>
                <textarea
                  className="input-base"
                  rows={4}
                  value={adminNotes}
                  onChange={(e) => setAdminNotes(e.target.value)}
                  placeholder="Notes only you see..."
                  data-testid="admin-notes-input"
                />
                <button onClick={saveNotes} className="btn-primary mt-3 w-full" data-testid="save-notes-button">
                  Save notes
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function Stat({ label, v, accent, negative }) {
  return (
    <div className="p-4 rounded-2xl" style={{ background: "var(--surface2)", border: "1px solid var(--border)" }}>
      <div className="font-mono text-xs uppercase tracking-wider" style={{ color: "var(--text-muted)" }}>{label}</div>
      <div className="font-mono text-lg mt-1" style={{ color: negative ? "var(--red)" : accent ? "var(--lime)" : "var(--text)" }}>{v}</div>
    </div>
  );
}

function BrickedCard({ dealId, bricked, onResult }) {
  const [busy, setBusy] = useState(false);

  const run = async (force = false) => {
    setBusy(true);
    try {
      const { data } = await api.post(`/deals/${dealId}/bricked-comp${force ? "?force=true" : ""}`);
      onResult(data);
      toast.success("Team comps ready");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Team comp run failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="surface-card p-6" data-testid="bricked-card">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <div className="flex items-center gap-2">
          <Home size={15} style={{ color: "var(--lime)" }} />
          <h2 className="font-mono text-xs uppercase tracking-wider" style={{ color: "var(--lime)" }}>
            AI comps
          </h2>
        </div>
        {bricked ? (
          <div className="flex items-center gap-2">
            <button
              onClick={async () => {
                try {
                  const res = await api.get(`/deals/${dealId}/comp-report`, { responseType: "blob" });
                  const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
                  const a = document.createElement("a");
                  a.href = url;
                  a.download = `Comp_Report.pdf`;
                  document.body.appendChild(a);
                  a.click();
                  a.remove();
                  window.URL.revokeObjectURL(url);
                } catch (e) {
                  toast.error("Failed to generate report");
                }
              }}
              className="btn-ghost text-xs flex items-center gap-1.5"
              data-testid="comp-report-download"
            >
              <FileText size={12} /> Download Comp Report
            </button>
            <button onClick={() => run(true)} disabled={busy} className="btn-ghost text-xs flex items-center gap-1.5" data-testid="bricked-rerun-button">
              {busy ? <span className="spinner" /> : <RefreshCw size={12} />} Re-run
            </button>
          </div>
        ) : (
          <button onClick={() => run(false)} disabled={busy} className="btn-primary text-sm flex items-center gap-2" data-testid="bricked-run-button">
            {busy ? <span className="spinner" /> : <Home size={14} />}
            {busy ? "Comping... ~20s" : "Run team comps"}
          </button>
        )}
      </div>

      {!bricked && !busy && (
        <p className="text-sm" style={{ color: "var(--text-muted)" }}>
          Pull ARV, market value, repair estimate and comps for this address from bricked.ai. Takes ~20-30 seconds and uses one comp credit.
        </p>
      )}
      {busy && !bricked && (
        <p className="text-sm font-mono" style={{ color: "var(--text-dim)" }}>
          Bricked is comping the property. Hang tight...
        </p>
      )}

      {bricked && (
        <div data-testid="bricked-results">
          <div className="grid sm:grid-cols-3 gap-4 mb-4">
            <Stat label="Team ARV" v={fmtMoney(bricked.arv)} accent />
            <Stat label="Market Value" v={fmtMoney(bricked.cmv)} />
            <Stat label="Repair Estimate" v={fmtMoney(bricked.total_repair_cost)} />
          </div>

          {bricked.repairs?.length > 0 && (
            <div className="mb-4">
              <div className="font-mono text-[10px] uppercase tracking-wider mb-2" style={{ color: "var(--text-dim)" }}>Itemized repairs</div>
              <div className="space-y-1.5">
                {bricked.repairs.map((r, i) => (
                  <div key={i} className="flex items-center justify-between px-3 py-2 rounded-2xl text-sm" style={{ background: "var(--surface2)", border: "1px solid var(--border)" }}>
                    <span>{r.repair || r.description}</span>
                    <span className="font-mono text-xs" style={{ color: "var(--lime)" }}>{fmtMoney(r.cost)}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {bricked.comps?.length > 0 && (
            <div className="mb-4">
              <div className="font-mono text-[10px] uppercase tracking-wider mb-2" style={{ color: "var(--text-dim)" }}>Comps ({bricked.comps.length})</div>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-left font-mono text-[10px] uppercase tracking-wider" style={{ color: "var(--text-dim)" }}>
                      <th className="py-2 pr-4">Address</th>
                      <th className="py-2 pr-4">Bd/Ba</th>
                      <th className="py-2 pr-4">SqFt</th>
                      <th className="py-2 pr-4">Sold</th>
                      <th className="py-2">Adjusted</th>
                    </tr>
                  </thead>
                  <tbody>
                    {bricked.comps.map((c, i) => (
                      <tr key={i} style={{ borderTop: "1px solid var(--border)" }}>
                        <td className="py-2.5 pr-4">{c.address || "—"}</td>
                        <td className="py-2.5 pr-4 font-mono text-xs">{c.beds ?? "—"}/{c.baths ?? "—"}</td>
                        <td className="py-2.5 pr-4 font-mono text-xs">{c.sqft ? Number(c.sqft).toLocaleString() : "—"}</td>
                        <td className="py-2.5 pr-4 font-mono text-xs">{c.last_sale_amount ? fmtMoney(c.last_sale_amount) : "—"}</td>
                        <td className="py-2.5 font-mono text-xs" style={{ color: "var(--lime)" }}>{c.adjusted_value ? fmtMoney(c.adjusted_value) : "—"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          <div className="flex items-center justify-between flex-wrap gap-2">
            {bricked.share_link && (
              <a href={bricked.share_link} target="_blank" rel="noreferrer" className="text-xs font-mono flex items-center gap-1.5" style={{ color: "var(--lime)" }} data-testid="bricked-share-link">
                <ExternalLink size={12} /> View full report
              </a>
            )}
            <span className="font-mono text-[10px]" style={{ color: "var(--text-dim)" }}>
              Comped {fmtDate(bricked.fetched_at)}
            </span>
          </div>
        </div>
      )}
    </div>
  );
}

const EDIT_FIELDS = [
  { name: "property_address", label: "Property address" },
  { name: "property_type", label: "Property type" },
  { name: "condition", label: "Condition", type: "select", options: ["", "Light Rehab", "Medium Rehab", "Heavy Rehab", "Full Rehab", "Turnkey"] },
  { name: "beds", label: "Beds" },
  { name: "baths", label: "Baths" },
  { name: "arv", label: "ARV ($)", kind: "number" },
  { name: "asking_price", label: "Asking price ($)", kind: "number" },
  { name: "repair_estimate", label: "Repair estimate ($)", kind: "number" },
  { name: "assignment_fee", label: "Assignment fee goal ($)", kind: "number" },
  { name: "closing_date", label: "Closing date", type: "date" },
  { name: "contact_name", label: "Seller contact name" },
  { name: "contact_phone", label: "Seller contact phone" },
  { name: "photos_link", label: "Photos link" },
  { name: "seller_motivation", label: "Seller situation", kind: "textarea" },
  { name: "desired_outcome", label: "What you want help with", kind: "textarea" },
  { name: "notes", label: "Notes", kind: "textarea" },
];

function EditDealModal({ deal, onClose, onSaved }) {
  const [form, setForm] = useState(() =>
    Object.fromEntries(EDIT_FIELDS.map((f) => [f.name, deal[f.name] ?? ""]))
  );
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    document.body.style.overflow = "hidden";
    const onEsc = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onEsc);
    return () => {
      document.body.style.overflow = "";
      window.removeEventListener("keydown", onEsc);
    };
  }, [onClose]);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    if (!String(form.property_address || "").trim()) {
      toast.error("Property address is required");
      return;
    }
    setSaving(true);
    try {
      const body = {};
      EDIT_FIELDS.forEach((f) => {
        const v = form[f.name];
        body[f.name] = f.kind === "number" ? (v === "" || v === null ? null : Number(String(v).replace(/,/g, ""))) : v;
      });
      const { data } = await api.put(`/deals/${deal.id}`, body);
      toast.success("Submission updated", { description: "Your coach can see the corrected numbers." });
      onSaved(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save changes");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{ background: "rgba(0,0,0,0.8)", backdropFilter: "blur(6px)" }}
      onClick={onClose}
      data-testid="edit-deal-modal"
    >
      <div
        className="w-full max-w-2xl rounded-3xl flex flex-col"
        style={{ background: "var(--surface)", border: "1px solid var(--border)", maxHeight: "90vh" }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between p-6 border-b flex-shrink-0" style={{ borderColor: "var(--border)" }}>
          <div className="flex items-center gap-2">
            <Pencil size={16} style={{ color: "var(--lime)" }} />
            <h2 className="font-mono text-sm uppercase tracking-wider" style={{ color: "var(--lime)" }}>Edit submission</h2>
          </div>
          <button onClick={onClose} className="p-1 rounded hover:bg-white/5" data-testid="edit-deal-close"><X size={18} /></button>
        </div>

        <div className="p-6 grid sm:grid-cols-2 gap-4 overflow-y-auto flex-1">
          {EDIT_FIELDS.map((f) => (
            <div key={f.name} className={f.kind === "textarea" ? "sm:col-span-2" : undefined}>
              <label className="field-label">{f.label}</label>
              {f.kind === "textarea" ? (
                <textarea
                  className="input-base"
                  rows={2}
                  value={form[f.name] ?? ""}
                  onChange={(e) => update(f.name, e.target.value)}
                  data-testid={`edit-deal-${f.name}`}
                />
              ) : f.type === "select" ? (
                <select
                  className="input-base"
                  value={form[f.name] ?? ""}
                  onChange={(e) => update(f.name, e.target.value)}
                  data-testid={`edit-deal-${f.name}`}
                >
                  {f.options.map((o) => <option key={o} value={o}>{o || "Select..."}</option>)}
                </select>
              ) : (
                <input
                  className="input-base"
                  type={f.type || "text"}
                  inputMode={f.kind === "number" ? "decimal" : undefined}
                  value={form[f.name] ?? ""}
                  onChange={(e) => update(f.name, f.kind === "number" ? e.target.value.replace(/[^0-9.,]/g, "") : e.target.value)}
                  data-testid={`edit-deal-${f.name}`}
                />
              )}
            </div>
          ))}
        </div>

        <div className="p-6 border-t flex items-center justify-end gap-3 flex-shrink-0" style={{ borderColor: "var(--border)" }}>
          <button onClick={onClose} className="btn-ghost text-sm" data-testid="edit-deal-cancel">Cancel</button>
          <button onClick={save} disabled={saving} className="btn-primary text-sm" data-testid="edit-deal-save">
            {saving ? "Saving..." : "Save changes"}
          </button>
        </div>
      </div>
    </div>
  );
}

const FIELD_LABELS = {
  property_address: "Property address",
  property_type: "Property type",
  condition: "Condition",
  beds: "Beds",
  baths: "Baths",
  arv: "ARV",
  asking_price: "Asking price",
  repair_estimate: "Repair estimate",
  assignment_fee: "Assignment fee goal",
  closing_date: "Closing date",
  contact_name: "Seller contact name",
  contact_phone: "Seller contact phone",
  photos_link: "Photos link",
  seller_motivation: "Seller situation",
  desired_outcome: "What you want help with",
  notes: "Notes",
};

const MONEY_FIELDS = new Set(["arv", "asking_price", "repair_estimate", "assignment_fee"]);

const showVal = (field, v) => {
  if (v === null || v === undefined || v === "") return "empty";
  if (MONEY_FIELDS.has(field)) return fmtMoney(Number(v));
  const str = String(v);
  return str.length > 80 ? `${str.slice(0, 80)}...` : str;
};

function EditHistory({ entries }) {
  if (!entries || entries.length === 0) return null;
  return (
    <div className="surface-card p-6" data-testid="deal-edit-history">
      <h2 className="font-mono text-xs uppercase tracking-wider mb-4" style={{ color: "var(--lime)" }}>
        Edit history
      </h2>
      <div className="space-y-5">
        {entries.map((e) => (
          <div key={e.id} data-testid={`deal-edit-${e.id}`}>
            <div className="text-sm">
              <span style={{ fontWeight: 600 }}>{e.actor_name}</span>
              <span style={{ color: "var(--text-muted)" }}>{e.actor_is_admin ? " (coach)" : ""} edited this</span>
            </div>
            <div className="font-mono text-[11px] mb-2" style={{ color: "var(--text-dim)" }}>
              {new Date(e.created_at).toLocaleString(undefined, { month: "short", day: "numeric", year: "numeric", hour: "numeric", minute: "2-digit" })}
            </div>
            <ul className="space-y-1.5">
              {e.changes.map((c, i) => (
                <li key={i} className="text-sm flex flex-wrap items-baseline gap-x-2">
                  <span style={{ color: "var(--text-muted)" }}>{FIELD_LABELS[c.field] || c.field}:</span>
                  <span className="line-through" style={{ color: "var(--text-dim)" }}>{showVal(c.field, c.from)}</span>
                  <span style={{ color: "var(--text-dim)" }}>&rarr;</span>
                  <span style={{ color: "var(--lime)" }}>{showVal(c.field, c.to)}</span>
                </li>
              ))}
            </ul>
          </div>
        ))}
      </div>
    </div>
  );
}

/** Renders the AI's plain text, turning any stray markdown into real headings and bold. */
function AiText({ text }) {
  if (!text) return null;
  const blocks = String(text).split(/\n{2,}/);
  return (
    <div className="space-y-3">
      {blocks.map((block, i) => {
        const heading = block.match(/^\s*#{1,6}\s*(.+)$/);
        if (heading) {
          return (
            <div key={i} className="font-mono text-xs uppercase tracking-wider pt-1" style={{ color: "var(--lime)" }}>
              {heading[1].replace(/\*\*/g, "")}
            </div>
          );
        }
        return (
          <p key={i} className="whitespace-pre-wrap">
            {block.split(/(\*\*[^*]+\*\*)/g).map((part, j) =>
              part.startsWith("**") && part.endsWith("**") ? (
                <strong key={j} style={{ color: "var(--text)" }}>{part.slice(2, -2)}</strong>
              ) : (
                part
              )
            )}
          </p>
        );
      })}
    </div>
  );
}
